import React, { memo, useCallback, useMemo } from 'react';
import { View, Text, Pressable, TouchableOpacity, Image, StyleSheet } from 'react-native';
import type { CorkBoardThreadSummary } from '@/types/residents/corkBoard';
import { TagBadge } from './TagBadge';
import { CORK_BOARD_COLORS, corkBoardStyles } from './corkBoardStyles';
import { SEMANTIC_COLORS, PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { formatRelativeDate } from '@/utils/common/formatting';

interface ThreadCardProps {
  thread: CorkBoardThreadSummary;
  onPress: (thread: CorkBoardThreadSummary) => void;
  onTagPress?: (tagId: number) => void;
}

export const ThreadCard: React.FC<ThreadCardProps> = memo(({ thread, onPress, onTagPress }) => {
  const isAnnouncement = thread.isAnnouncement || thread.isPinned;

  const handlePress = useCallback(() => onPress(thread), [onPress, thread]);

  const accessibilityLabel = useMemo(() => {
    const parts: string[] = [];
    if (thread.isAnnouncement) parts.push('Announcement');
    else if (thread.isPinned) parts.push('Pinned');
    parts.push(thread.title);
    parts.push(`by ${thread.creator.name}`);
    parts.push(`${thread.replyCount} ${thread.replyCount === 1 ? 'reply' : 'replies'}`);
    parts.push(`${thread.viewCount} ${thread.viewCount === 1 ? 'view' : 'views'}`);
    if (thread.isLocked) parts.push('Locked');
    return parts.join(', ');
  }, [thread]);

  return (
    <Pressable
      style={({ pressed }) => [
        styles.container,
        isAnnouncement && styles.announcementContainer,
        pressed && styles.pressed,
      ]}
      onPress={handlePress}
      accessibilityRole="link"
      accessibilityLabel={accessibilityLabel}
      accessibilityHint="Opens thread details"
    >
      {/* Announcement / Pin indicator */}
      {isAnnouncement && (
        <View style={styles.announcementBanner}>
          <Text style={styles.announcementLabel}>
            {thread.isAnnouncement ? 'ANNOUNCEMENT' : 'PINNED'}
          </Text>
        </View>
      )}

      {/* Tags row */}
      {thread.tags.length > 0 && (
        <View style={styles.tagsRow}>
          {thread.tags.map((tag) => (
            <TagBadge
              key={tag.id}
              tag={tag}
              size="small"
              onPress={onTagPress ? () => onTagPress(Number(tag.id)) : undefined}
            />
          ))}
        </View>
      )}

      {/* Title */}
      <Text style={styles.title} numberOfLines={2}>{thread.title}</Text>

      {/* Body preview */}
      {thread.bodyPreview ? (
        <Text style={styles.bodyPreview} numberOfLines={2}>{thread.bodyPreview}</Text>
      ) : null}

      {/* Preview image */}
      {thread.previewImageUrl ? (
        <Image
          source={{ uri: thread.previewImageUrl }}
          style={styles.previewImage}
          resizeMode="cover"
          accessibilityIgnoresInvertColors
        />
      ) : null}

      {/* Footer: author + timestamp + stats */}
      <View style={styles.footer}>
        <View style={styles.authorSection}>
          {thread.creator.photoUrl ? (
            <Image source={{ uri: thread.creator.photoUrl }} style={styles.avatarImage} accessibilityIgnoresInvertColors />
          ) : (
            <View style={corkBoardStyles.avatar}>
              <Text style={corkBoardStyles.avatarText}>{thread.creator.initials}</Text>
            </View>
          )}
          <Text style={styles.authorName} numberOfLines={1}>{thread.creator.name}</Text>
          <Text style={styles.dot}>·</Text>
          <Text style={styles.timestamp}>{formatRelativeDate(thread.lastActivityAt)}</Text>
        </View>

        <View style={styles.statsSection}>
          {thread.attachmentCount > 0 && (
            <Text style={styles.stat}>{thread.attachmentCount} files</Text>
          )}
          <Text style={styles.stat}>{thread.replyCount} replies</Text>
          <Text style={styles.stat}>{thread.viewCount} views</Text>
        </View>
      </View>

      {/* Lock indicator */}
      {thread.isLocked && (
        <View style={styles.lockIndicator}>
          <RESIDENTS_SVG_COMPONENTS.PADLOCK width={12} height={12} color={SEMANTIC_COLORS.text.muted} />
          <Text style={styles.lockText}>Replies disabled</Text>
        </View>
      )}
    </Pressable>
  );
});

const styles = StyleSheet.create({
  container: {
    backgroundColor: CORK_BOARD_COLORS.cardBackground,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: CORK_BOARD_COLORS.cardBorder,
    padding: 16,
    gap: 8,
    marginHorizontal: 16,
  },
  pressed: {
    opacity: 0.7,
  },
  announcementContainer: {
    borderColor: CORK_BOARD_COLORS.announcementBorder,
    backgroundColor: CORK_BOARD_COLORS.announcementBg,
  },
  announcementBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  announcementLabel: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.8,
    color: CORK_BOARD_COLORS.announcementBorder,
  },
  tagsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  title: {
    fontSize: 15,
    fontWeight: '600',
    color: SEMANTIC_COLORS.text.primary,
    lineHeight: 21,
  },
  bodyPreview: {
    fontSize: 13,
    color: SEMANTIC_COLORS.text.secondary,
    lineHeight: 19,
  },
  previewImage: {
    width: '100%',
    height: 160,
    borderRadius: 10,
    backgroundColor: PRIMITIVE_COLORS.gray850,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 2,
  },
  authorSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flex: 1,
    minWidth: 0,
  },
  authorName: {
    fontSize: 12,
    color: SEMANTIC_COLORS.text.secondary,
    flexShrink: 1,
  },
  dot: {
    fontSize: 12,
    color: SEMANTIC_COLORS.text.muted,
  },
  timestamp: {
    fontSize: 12,
    color: SEMANTIC_COLORS.text.muted,
  },
  statsSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  stat: {
    fontSize: 12,
    color: SEMANTIC_COLORS.text.muted,
  },
  lockIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingTop: 4,
    borderTopWidth: 1,
    borderTopColor: PRIMITIVE_COLORS.gray800,
  },
  lockText: {
    fontSize: 11,
    color: SEMANTIC_COLORS.text.muted,
    fontStyle: 'italic',
  },
  avatarImage: {
    width: 28,
    height: 28,
    borderRadius: 14,
  },
});
